import IconButton from '@material-ui/core/IconButton';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import TablePagination from '@material-ui/core/TablePagination';
import KeyboardArrowLeft from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRight from '@material-ui/icons/KeyboardArrowRight';
import { StandardDivProps } from '@sprinx/react-mui-components/StandardDiv';
import withThemeProps from '@sprinx/react-mui-components/withThemeProps';
import clsx from 'clsx';
import React from 'react';
import { useRecoilState } from 'recoil';
import { cataloguePaginationState } from '../../../api/products/catalogue';
import useTranslateWithFallback from '../../../hooks/useTranslateWithFallback';

export type CataloguePageListPaginationProps = StandardDivProps<CataloguePageListPaginationClassKey>;

export type CataloguePageListPaginationClassKey = 'root' | 'pagination' | 'select' | 'actions' | 'numberField';

const themeSettings = { name: 'CataloguePageListPagination' };
const useStyles = makeStyles(
  (theme: Theme) =>
    createStyles<CataloguePageListPaginationClassKey, {}>({
      root: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('xs')]: {
          borderBottom: `1px solid ${theme.palette.divider}`,
        },
      },
      pagination: {
        display: 'flex',
        flexDirection: 'column',
        [theme.breakpoints.down('xs')]: {
          '& > div': {
            display: 'flex',
            flexDirection: 'column',
            padding: theme.spacing(2, 0),
            '& > div': {
              margin: 'auto',
              border: '1px solid gray',
              borderRadius: '5px',
            },
          },
        },
      },
      select: {
        color: theme.palette.primary.light,
        fontWeight: theme.typography.fontWeightBold,
        fontSize: theme.typography.subtitle1.fontSize,
      },
      actions: {
        flexShrink: 0,
        color: theme.palette.text.secondary,
        marginLeft: theme.spacing(2.5),
      },
      numberField: {
        fontSize: theme.typography.subtitle1.fontSize,
        width: '130px',
        marginRight: theme.spacing(2),
        marginBottom: '25px',
        textAlign: 'center',
        verticalAlign: 'middle',
      },
    }),
  themeSettings,
);

const CataloguePageListPagination = React.forwardRef<HTMLDivElement, CataloguePageListPaginationProps>(
  ({ className, classes: pClasses }, ref) => {
    const classes = useStyles({ classes: pClasses });
    const [pagination, setPagination] = useRecoilState(cataloguePaginationState);
    const count = pagination.total || 0;
    const page = (pagination.page || 1) - 1;
    const maxPage = Math.ceil(count / (pagination.rowsPerPage || 24)) - 1;
    const t = useTranslateWithFallback();

    const handlePageChange = (_event: any, nextPage: number) => {
      setPagination({ page: nextPage + 1 });
      window.scrollTo(0, 0);
    };

    return count === 0 ? null : (
      <div ref={ref} className={clsx(classes.root, className)}>
        <TablePagination
          labelRowsPerPage={t('app/itemsPerPage')}
          labelDisplayedRows={() =>
            `${count} ${t('common/items')} — ${t('common/page')} ${page + 1} ${t('common/of')} ${maxPage + 1}`
          }
          component='div'
          count={count}
          page={count === 0 ? 0 : page}
          className={classes.pagination}
          classes={{
            select: classes.select,
            selectIcon: classes.select,
            actions: classes.select,
          }}
          rowsPerPage={pagination.rowsPerPage || 24}
          rowsPerPageOptions={[6, 12, 24, 48]}
          onChangePage={handlePageChange}
          onChangeRowsPerPage={(event: React.ChangeEvent<any>) => {
            const nextRowsPerPage = parseInt(event.target.value, 10);
            setPagination({ page: 1, rowsPerPage: nextRowsPerPage });
          }}
          ActionsComponent={(subProps) => (
            <div className={classes.actions}>
              <IconButton
                onClick={(e) => subProps.onChangePage(e, page - 1)}
                disabled={page === 0}
                aria-label='Previous Page'
              >
                <KeyboardArrowLeft />
              </IconButton>
              <IconButton
                onClick={(e) => subProps.onChangePage(e, page + 1)}
                disabled={page >= maxPage}
                aria-label='Next Page'
              >
                <KeyboardArrowRight />
              </IconButton>
            </div>
          )}
        />
      </div>
    );
  },
);

CataloguePageListPagination.displayName = 'CataloguePageListPagination';

export default withThemeProps(themeSettings)(CataloguePageListPagination);
